"use client";

import { router } from "@inertiajs/react";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";

import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { useDataTable } from "@/hooks/use-data-table";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import { organizationCategoriesPath, organizationCategoryPath } from "@/routes";
import type { Category, PagyMetadata, RansackFilters } from "@/types";

import { buildColumns } from "./columns";

interface CategoriesDataTableProps {
  categories: Category[];
  pagy: PagyMetadata;
  filters: RansackFilters;
  organizationSlug: string;
}

export function CategoriesDataTable({
  categories,
  pagy,
  filters,
  organizationSlug,
}: CategoriesDataTableProps) {
  // Use shared data table logic for common operations
  const {
    isLoading,
    handleFilterChange,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({
    filters,
    pagy,
    path: organizationCategoriesPath(organizationSlug),
  });
  const columns = buildColumns(organizationSlug);

  const {
    value: nameSearch,
    setValue: setNameSearch,
    debouncedValue: debouncedName,
  } = useSyncedDebouncedValue(filters.name_cont ?? "");
  const {
    value: descriptionSearch,
    setValue: setDescriptionSearch,
    debouncedValue: debouncedDescription,
  } = useSyncedDebouncedValue(filters.description_cont ?? "");

  useEffect(() => {
    if ((filters.name_cont ?? "") !== debouncedName) {
      handleFilterChange({ name_cont: debouncedName || undefined });
    }
    // Only react to debounced local changes, not server-driven filter updates.
  }, [debouncedName]);

  useEffect(() => {
    if ((filters.description_cont ?? "") !== debouncedDescription) {
      handleFilterChange({
        description_cont: debouncedDescription || undefined,
      });
    }
    // Same pattern for description: depend only on debounced value.
  }, [debouncedDescription]);

  const clearFilters = () => {
    setNameSearch("");
    setDescriptionSearch("");
    handleFilterChange({
      name_cont: undefined,
      description_cont: undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* External Filters */}
      <div className="flex items-center gap-4">
        <ButtonGroup>
          <InputGroup className="w-[280px]">
            <InputGroupAddon>
              <InputGroupText>Nombre</InputGroupText>
            </InputGroupAddon>
            <InputGroupInput
              placeholder="Buscar por nombre..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
          </InputGroup>
          <InputGroup className="w-[280px] rounded-l-none border-l-0">
            <InputGroupAddon>
              <InputGroupText>Descripción</InputGroupText>
            </InputGroupAddon>
            <InputGroupInput
              placeholder="Buscar por descripción..."
              value={descriptionSearch}
              onChange={(e) => setDescriptionSearch(e.target.value)}
            />
          </InputGroup>
        </ButtonGroup>

        {(nameSearch || descriptionSearch) && (
          <Button variant="outline" onClick={clearFilters}>
            <Trash2 className="h-4 w-4" /> Limpiar filtros
          </Button>
        )}
      </div>

      {/* DataTable */}
      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns}
          data={categories}
          sorting={getSortingState()}
          onSortingChange={handleSortingChange}
          manualSorting={true}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false}
          onRowClick={(category) =>
            router.visit(organizationCategoryPath(organizationSlug, category))
          }
        />
      </div>

      {/* Server-side pagination */}
      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="categorías"
      />
    </div>
  );
}
